import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { getSsoUrl, ssoLogin, mergeGuestCartWithUserCart, checkUserExists } from '@/apiEndpoints';
import type { LoginFormValues } from '@/types';
import Footer from '@/components/Footer';
import MobileBottomNav from '@/components/MobileBottomNav';
import Google from '@/icons/Google';
import Apple from '@/icons/Apple';
import LogoBigColor from '@/icons/LogoBigColor';
import LogoBigWhite from '@/icons/LogoBigWhite';
import { useAuth } from '@/contexts/auth-context';
import { Input } from '@heroui/input';
import { Button, Checkbox } from '@heroui/react';
import { pageBg, wrap, darkPanel, eyebrow } from '@/components/ui/design';
import { getSsoErrorMessage, isPasswordAccountConflict, retrySsoAfterLink, clearSsoRetry } from '@/utils/authMessages';

const fieldClassNames = {
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
    inputWrapper: 'h-12 rounded-2xl bg-grey99 px-4 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.08] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
};
const pillShape = '[&.transform-gpu.tap-highlight-transparent]:!rounded-full [&.transform-gpu.transform-gpu.tap-highlight-transparent]:!h-12';
const primaryPill = `no-global-button-color w-full ${pillShape} bg-grey10 text-white hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90`;
const ghostPill = `no-global-button-color w-full gap-2 ${pillShape} border-small border-grey90 bg-transparent text-grey10 hover:bg-grey99 dark:border-white/15 dark:text-white dark:hover:bg-white/[0.06]`;
const linkAccent = 'font-semibold text-grey10 underline decoration-grey90 underline-offset-4 transition-colors hover:decoration-grey10 dark:text-white dark:decoration-white/30 dark:hover:decoration-white';

function BrandPanel() {
    return (
        <aside className={`${darkPanel} hidden min-h-[640px] flex-col justify-between p-10 md:flex lg:p-14`}>
            <img
                src="/dancer.png"
                alt="Dancer in Persian arch"
                className="absolute inset-0 -z-20 h-full w-full object-cover object-center opacity-35"
            />
            <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/30 dark:from-[#101010] dark:via-[#101010]/80 dark:to-[#101010]/30" />
            <div className="pointer-events-none absolute -right-32 -top-32 -z-10 h-[420px] w-[420px] rounded-full bg-nightAccent/30 blur-[100px] max-md:hidden" />
            <div className="pointer-events-none absolute -bottom-40 left-1/4 -z-10 h-[360px] w-[360px] rounded-full bg-[#6d4aff]/25 blur-[100px] max-md:hidden" />
            <LogoBigWhite height={44} width={180} />
            <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-nightAccent">Welcome back</p>
                <h2 className="mt-4 text-[clamp(44px,5vw,72px)] font-bold leading-[0.95] tracking-[-0.05em]">
                    Find your next <span className="text-nightAccent">night out.</span>
                </h2>
                <p className="mt-6 max-w-sm text-lg leading-relaxed text-white/60">
                    Concerts, parties and culture near you, with every ticket in one place.
                </p>
            </div>
        </aside>
    );
}

export default function Login() {
    const [form, setForm] = useState<LoginFormValues>({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [emailValidationMessage, setEmailValidationMessage] = useState<{ type: 'info' | 'warning'; text: string } | null>(null);
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);
    const [isLoginDisabled, setIsLoginDisabled] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasNumber: false,
        hasLetter: false,
        isValid: false
    });
    const [showForgotHint, setShowForgotHint] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { login, isLoading, guestCartId, clearGuestCartId } = useAuth();

    // Fetch SSO URLs
    const { data: ssoData, isLoading: ssoLoading } = useQuery({
        queryKey: ['ssoUrls'],
        queryFn: getSsoUrl,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    const mutation = useMutation({
        mutationFn: async (values: LoginFormValues) => {
            let email = values.email;
            // Map email to username for the API
            const response = await api.post('/auth/login', { email: email, password: values.password });
            return response.data;
        },
        onSuccess: async (data) => {
            // Show success message
            setMessage({ type: 'success', text: 'Login successful! Redirecting...' });

            // Set tokens from Cognito-style response
            if (
                data &&
                data.accessToken?.jwtToken &&
                data.idToken?.jwtToken &&
                data.refreshToken?.token
            ) {
                await login({
                    access: data.accessToken.jwtToken,
                    id: data.idToken.jwtToken,
                    refresh: data.refreshToken.token,
                });

                // Check if user was a guest with cart items and merge cart
                if (guestCartId) {
                    try {
                        await mergeGuestCartWithUserCart(guestCartId);
                        clearGuestCartId(); // Clear guest cart ID from localStorage
                        setMessage({ type: 'success', text: 'Login successful! Your cart items have been merged. Redirecting...' });
                    } catch (error) {
                        console.error('Failed to merge guest cart:', error);
                        setMessage({ type: 'success', text: 'Login successful! Redirecting...' });
                    }
                }

                navigate((location.state as { from?: string } | null)?.from || '/');
            }
        },
        onError: (error: any) => {
            // Show user-friendly error message
            let errorMessage = 'Login failed. Please try again.';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = 'Invalid email or password. Please check your credentials.';
            } else if (error.response?.status === 404) {
                errorMessage = 'User not found. Please check your email address.';
            } else if (error.response?.status >= 500) {
                errorMessage = 'Server error. Please try again later.';
            }

            setMessage({ type: 'error', text: errorMessage });
        }
    });

    useEffect(() => {
        if ((location.state as { passwordReset?: boolean } | null)?.passwordReset) {
            setMessage({ type: 'success', text: 'Your password has been reset. Log in with your new password.' });
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.state, location.pathname, navigate]);

    function validatePassword(password: string) {
        const minLength = password.length >= 8;
        const hasNumber = /\d/.test(password);
        const hasLetter = /[a-zA-Z]/.test(password);
        const isValid = minLength && hasNumber && hasLetter;

        setPasswordValidation({
            minLength,
            hasNumber,
            hasLetter,
            isValid
        });
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
        // Clear email validation message and reset login state when user types
        if (e.target.name === 'email') {
            setEmailValidationMessage(null);
            setIsLoginDisabled(false);
        }
        // Validate password in real-time
        if (e.target.name === 'password') {
            validatePassword(e.target.value);
        }
    }

    async function handleEmailBlur() {

        if (form.email.includes(':')) {
            console.log(form.email);

            setEmailValidationMessage(null);
            setIsLoginDisabled(false);
            return
        }

        if (!form.email || !form.email.includes('@')) {
            return;
        }

        setIsValidatingEmail(true);
        setEmailValidationMessage(null);
        setIsLoginDisabled(false);

        try {
            const response = await checkUserExists(form.email);

            if (response.exists) {
                if (response.provider === 'cognito') {
                    // User exists and is a Cognito user, show nothing and enable login
                    setEmailValidationMessage(null);
                    setIsLoginDisabled(false);
                } else if (response.provider === 'google') {
                    // User exists and has Google SSO, guide them to Google and disable login
                    setEmailValidationMessage({
                        type: 'warning',
                        text: 'Use Google sign-in below.'
                    });
                    setIsLoginDisabled(true);
                } else if (response.provider === 'apple') {
                    // User exists and has Apple SSO, guide them to Apple and disable login
                    setEmailValidationMessage({
                        type: 'warning',
                        text: 'Use Apple sign-in below.'
                    });
                    setIsLoginDisabled(true);
                } else {
                    // User exists but is not a Cognito user, guide them to SSO and disable login
                    setEmailValidationMessage({
                        type: 'warning',
                        text: 'Use Google or Apple sign-in below.'
                    });
                    setIsLoginDisabled(true);
                }
            } else {
                // User doesn't exist, guide them to create account and disable login
                setEmailValidationMessage({
                    type: 'warning',
                    text: 'Email does not exist. Use email/password or Google/Apple to create an account.'
                });
                setIsLoginDisabled(true);
            }
        } catch (error: any) {
            console.error('Email validation error:', error);
            // Don't show error to user, just silently fail
            setEmailValidationMessage(null);
        } finally {
            setIsValidatingEmail(false);
        }
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setMessage(null);
        setShowForgotHint(false);
        mutation.mutate(form);
    }

    function handleSsoLogin(provider: string) {
        if (ssoData && ssoData[provider]) {
            window.location.href = ssoData[provider];
        }
    }

    useEffect(() => {
        const handleSsoCallback = async () => {
            const code = searchParams.get('code');
            const provider = searchParams.get('provider') || 'google';
            if (!code || typeof code !== 'string') return;

            try {
                setMessage({ type: 'success', text: `Processing ${provider} login...` });

                const response = await ssoLogin({
                    authenticationCode: code,
                    provider: provider as 'google' | 'apple'
                });
                if (response?.status && response.status >= 200 && response.status < 300 && response?.data) {
                    const { id_token, access_token, refresh_token } = response.data;
                    clearSsoRetry();

                    // Set tokens using the auth context
                    await login({
                        access: access_token,
                        id: id_token,
                        refresh: refresh_token,
                    });

                    // Check if user was a guest with cart items and merge cart
                    if (guestCartId) {
                        try {
                            await mergeGuestCartWithUserCart(guestCartId);
                            clearGuestCartId(); // Clear guest cart ID from localStorage
                            setMessage({ type: 'success', text: `${provider} login successful! Your cart items have been merged. Redirecting...` });
                        } catch (error) {
                            console.error('Failed to merge guest cart:', error);
                            setMessage({ type: 'success', text: `${provider} login successful! Redirecting...` });
                        }
                    } else {
                        setMessage({ type: 'success', text: `${provider} login successful! Redirecting...` });
                    }

                    // Clear the URL parameters
                    navigate('/login', { replace: true });

                    // Redirect to home page
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 1000);
                } else {
                    throw new Error('Invalid response from SSO server');
                }
            } catch (error: any) {
                console.error('SSO login error:', error);
                if (await retrySsoAfterLink(error, provider)) return;
                const errorMessage = getSsoErrorMessage(error, provider);
                setShowForgotHint(isPasswordAccountConflict(errorMessage));
                setMessage({ type: 'error', text: errorMessage });

                // Clear the URL parameters on error
                navigate('/login', { replace: true });
            }
        };

        if (searchParams.get('code')) {
            handleSsoCallback();
        }
    }, [searchParams, login, navigate]);


    return (
        <div className={`min-h-screen flex flex-col overflow-hidden ${pageBg}`}>
            <main className={`${wrap} grid flex-1 gap-10 py-8 md:grid-cols-2 md:py-10 lg:gap-16`}>
                <BrandPanel />
                <section className="flex items-center justify-center">
                    <div className="no-global-input flex w-full max-w-[420px] flex-col gap-6 py-4 md:py-10">
                        <div className="md:hidden">
                            <LogoBigColor height={40} width={168} className="dark:hidden" />
                            <LogoBigWhite height={40} width={168} className="hidden dark:block" />
                        </div>
                        <div>
                            <p className={eyebrow}>Welcome back</p>
                            <h1 className="mt-3 text-[clamp(40px,6vw,56px)] font-bold leading-[0.95] tracking-[-0.05em] text-grey10 dark:text-white">Login</h1>
                        </div>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
                            <div className="relative">
                                <Input
                                    name="email"
                                    //type="email"
                                    placeholder="Email"
                                    value={form.email}
                                    onChange={handleChange}
                                    onBlur={handleEmailBlur}
                                    required
                                    classNames={fieldClassNames}
                                />
                                {isValidatingEmail && (
                                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary40 dark:border-nightAccent"></div>
                                    </div>
                                )}
                            </div>

                            {/* Email Validation Message */}
                            {emailValidationMessage && (
                                <div className={`p-4 rounded-2xl ring-1 text-sm font-medium ${emailValidationMessage.type === 'info'
                                    ? 'bg-green-50 dark:bg-white/[0.04] ring-green-200 dark:ring-white/10 text-green-800 dark:text-nightAccent'
                                    : 'bg-orange-50 dark:bg-white/[0.04] ring-orange-200 dark:ring-white/10 text-orange-800 dark:text-nightMuted'
                                    }`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${emailValidationMessage.type === 'info'
                                            ? 'bg-green-100 dark:bg-white/10'
                                            : 'bg-orange-100 dark:bg-white/10'
                                            }`}>
                                            {emailValidationMessage.type === 'info' ? (
                                                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                        <span className="text-sm">{emailValidationMessage.text}</span>
                                    </div>
                                </div>
                            )}

                            <Input
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                value={form.password}
                                onChange={handleChange}
                                required
                                isDisabled={isLoginDisabled}

                                endContent={
                                    <button
                                        type="button"
                                        className="text-grey40 dark:text-nightMuted hover:text-grey10 dark:hover:text-white"
                                        onClick={() => setShowPassword((v) => !v)}
                                        tabIndex={-1}
                                        aria-label="Toggle password visibility"
                                        disabled={isLoginDisabled}
                                    >
                                        {showPassword ? (
                                            <svg className="w-5 h-5" fill="none" stroke="#008C95" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="#008C95" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                            </svg>
                                        )}
                                    </button>
                                }
                                classNames={fieldClassNames}
                            />

                            {/* Password Validation */}
                            {/* {form.password && (
                                <div className="p-3 rounded border border-gray-200 bg-gray-50">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</div>
                                    <div className="space-y-1">
                                        <div className={`flex items-center gap-2 text-sm ${passwordValidation.minLength ? 'text-green-600' : 'text-red-500'}`}>
                                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.minLength ? 'bg-green-100' : 'bg-red-100'}`}>
                                                {passwordValidation.minLength ? (
                                                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                            At least 8 characters
                                        </div>
                                        <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasNumber ? 'bg-green-100' : 'bg-red-100'}`}>
                                                {passwordValidation.hasNumber ? (
                                                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                            Contains a number
                                        </div>
                                        <div className={`flex items-center gap-2 text-sm ${passwordValidation.hasLetter ? 'text-green-600' : 'text-red-500'}`}>
                                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasLetter ? 'bg-green-100' : 'bg-red-100'}`}>
                                                {passwordValidation.hasLetter ? (
                                                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                            Contains a letter
                                        </div>
                                    </div>
                                </div>
                            )} */}

                            <div className="flex items-center justify-between text-sm">
                                <label className="flex items-center gap-2 font-medium text-grey40 dark:text-nightMuted">
                                    <Checkbox type="checkbox" isSelected={rememberMe} onValueChange={setRememberMe} classNames={{
                                        label: "text-grey50 dark:text-nightMuted text-sm font-medium",
                                    }} />
                                    Remember me
                                </label>
                                <a href="/forgot-password" className={`text-sm ${linkAccent}`}>Forgot password?</a>
                            </div>
                            <Button
                                type="submit"
                                color="primary"
                                size="lg"
                                className={`mt-2 ${primaryPill}`}
                                isDisabled={mutation.isPending || isLoading || isLoginDisabled || !passwordValidation.isValid}
                                isLoading={mutation.isPending || isLoading}
                            >
                                {mutation.isPending || isLoading ? 'Logging in...' : 'Log in'}
                            </Button>
                        </form>

                        {/* User-friendly Message Display */}
                        {message && (
                            <div className={`p-4 rounded-2xl ring-1 text-sm ${message.type === 'success'
                                ? 'bg-green-50 dark:bg-white/[0.04] ring-green-200 dark:ring-white/10 text-green-800 dark:text-nightAccent'
                                : 'bg-red-50 dark:bg-white/[0.04] ring-red-200 dark:ring-white/10 text-red-800 dark:text-red-400'
                                }`}>
                                <div className="flex items-center gap-2">
                                    {message.type === 'success' ? (
                                        <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    <span className="font-medium">{message.text}</span>
                                </div>
                                {message.type === 'error' && showForgotHint && (
                                    <a href="/forgot-password" className={`mt-2 inline-block text-sm ${linkAccent}`}>Forgot password?</a>
                                )}
                            </div>
                        )}

                        <div className="flex items-center w-full">
                            <div className="flex-1 h-px bg-black/[0.06] dark:bg-white/10" />
                            <span className="mx-4 text-xs font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted">Or login with</span>
                            <div className="flex-1 h-px bg-black/[0.06] dark:bg-white/10" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <Button
                                variant="bordered"
                                size="md"
                                className={ghostPill}
                                onPress={() => handleSsoLogin('googleWeb')}
                                isDisabled={ssoLoading || !ssoData?.google}
                                isLoading={ssoLoading}
                            >
                                <Google />
                                {ssoLoading ? 'Loading...' : 'Google'}
                            </Button>
                            <Button
                                variant="bordered"
                                size="md"
                                className={ghostPill}
                                onPress={() => handleSsoLogin('appleWeb')}
                                isDisabled={ssoLoading || !ssoData?.apple}
                                isLoading={ssoLoading}
                            >
                                <Apple />
                                {ssoLoading ? 'Loading...' : 'Apple'}
                            </Button>
                        </div>
                        <div className="text-center text-sm text-grey40 dark:text-nightMuted">
                            Don&apos;t have an account?{' '}
                            <a href="/signup" className={linkAccent}>Create one</a>
                        </div>
                    </div>
                </section>
            </main>
            {/* Footer (Full Width) */}
            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
} 